/**
 * Tier-promotion / -demotion rules consumed by the ranker.
 *
 * Currently:
 *   1. CTE shadowing — promote a `kind: 'cte'` candidate to tier 1 when a
 *      same-name table/view sibling exists in the same candidate list.
 *   2. FK-driven JOIN snippet — promote `fkJoin: true` candidates to tier 1
 *      when the cursor is in a JOIN/FROM relation slot AND at least one
 *      alias is already bound; demote to tier 6 otherwise so misplaced FK
 *      snippets don't outrank real columns/relations.
 *   3. CTE-first at from-slots — an in-scope CTE is promoted to tier 1 at
 *      any `from` relation slot, shadowing sibling or not: the CTE the user
 *      just defined is almost always the relation they want next, and at
 *      its emitted tier 5 it ranked below every snapshot table.
 *   5. Caret-binding relation — the table/view/CTE whose binding the caret
 *      sits inside (`UPDATE ▮cheap SET …`, `FROM ▮dual`) is lifted to
 *      tier 1 whatever tier it was emitted at: the statement around the
 *      caret was authored against it. Ranker-level twin of the resolvers'
 *      `promoteCaretBindingRelation` (fkJoin/caretBindingPromotion.ts),
 *      which only runs on the from-slot branch — an UPDATE target is
 *      classified `set`, and a CTE arrives at tier 5 there.
 *   6. Column-first positions — a table/view emitted at tier 1 (bound /
 *      FK-linked / buffer-referenced) drops to tier 2 at select-list /
 *      where / group-by / having / order-by / join-on / set when the list
 *      also carries columns: `SELECT sal▮` wants `SalesOrderID`, not the
 *      bound `Sales.SalesOrderHeader` ahead of it.
 *   7. Column-first heads — a procedural local / routine parameter emitted
 *      at tier 1 drops to tier 2 at select-list / group-by / order-by when
 *      the list also carries columns. The head of a projection or a sort /
 *      grouping key is a column reference; inside a 1500-line PL/SQL body
 *      the alphabetical tier-1 merge put `c_*` cursors and `e_*` exceptions
 *      ahead of the FROM relation's columns and pushed them below the
 *      widget's fold (`CURSOR c IS SELECT ▮ … FROM employees`). WHERE / SET
 *      / HAVING / JOIN-ON are left alone: their value side is where a local
 *      is the likeliest answer (`WHERE employee_id = ▮p_employee_id`).
 *      Engine-agnostic — every dialect emits locals as `variable` /
 *      `parameter` at tier 1.
 *   4. FK-join snippet scope — a snippet that pairs against an alias whose
 *      binding starts AT or AFTER the caret (`FROM tag JOIN ▮Track t` offers
 *      `Album ON a.AlbumId = t.AlbumId`, but `t` is not in scope yet at the
 *      caret) drops to tier 2: the join it writes is invalid where the user
 *      is typing it, while snippets against already-bound aliases stay at 1.
 *
 * Future tier rules slot in here without further refactoring of `rank.ts`.
 */

import type {
  CompletionCandidate,
  CompletionCandidateTier,
  CompletionContext,
  CompletionPositionKind,
} from '@/core/engines';

import { caretBindingRelation, isCaretBindingLeader } from './leader';

const COLUMN_FIRST_POSITIONS: ReadonlySet<CompletionPositionKind> = new Set([
  'select-list',
  'where',
  'group-by',
  'having',
  'order-by',
  'join-on',
  'set',
]);

const COLUMN_HEAD_POSITIONS: ReadonlySet<CompletionPositionKind> = new Set([
  'select-list',
  'group-by',
  'order-by',
]);

export function applyTierPromotions(
  c: CompletionCandidate,
  allCandidates: readonly CompletionCandidate[],
  context: CompletionContext,
): CompletionCandidateTier {
  if (c.fkJoin) return fkJoinTier(c, context);
  if (
    (c.kind === 'table' || c.kind === 'view' || c.kind === 'cte') &&
    isCaretBindingLeader(c, c.label, caretBindingRelation(context))
  ) {
    return 1;
  }
  if (
    (c.kind === 'table' || c.kind === 'view') &&
    c.tier === 1 &&
    COLUMN_FIRST_POSITIONS.has(context.positionKind) &&
    allCandidates.some((other) => other.kind === 'column')
  ) {
    return 2;
  }
  if (
    (c.kind === 'variable' || c.kind === 'parameter') &&
    c.tier === 1 &&
    COLUMN_HEAD_POSITIONS.has(context.positionKind) &&
    allCandidates.some((other) => other.kind === 'column')
  ) {
    return 2;
  }
  if (c.kind === 'cte') {
    if (context.positionKind === 'from') return 1;
    const lower = c.label.toLowerCase();
    const shadowsRealRelation = allCandidates.some(
      (other) =>
        other !== c &&
        (other.kind === 'table' || other.kind === 'view') &&
        other.label.toLowerCase() === lower,
    );
    if (shadowsRealRelation) return 1;
  }
  return c.tier;
}

function fkJoinTier(
  c: CompletionCandidate,
  context: CompletionContext,
): CompletionCandidateTier {
  const inJoinPosition = context.positionKind === 'from';
  const hasBoundFrom = context.aliasBindings.length > 0;
  if (!inJoinPosition || !hasBoundFrom) return 6;
  return pairsWithAliasBoundBeforeCaret(c, context) ? 1 : 2;
}

function pairsWithAliasBoundBeforeCaret(
  c: CompletionCandidate,
  context: CompletionContext,
): boolean {
  if (!c.fkJoinFromAlias) return true;
  const lower = c.fkJoinFromAlias.toLowerCase();
  const binding = context.aliasBindings.find((b) => b.alias.toLowerCase() === lower);
  if (!binding) return true;
  return binding.definedAt.start < context.offset;
}
